import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CircularProgress, Tooltip } from '@mui/material';
import { round } from 'lodash';
import { startOfDay } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { useDebounce } from 'usehooks-ts';
import PageContainer from 'components/PageContainer';
import InvestmentDetails, {
  EditableInvestmentDetails,
} from './InvestmentDetails';
import CurrentShareholdings from './CurrentShareholdings';
import ExitedShareholdings from './ExitedShareholdings';
import CoreFees from './CoreFees';
import FutureFees from './FutureFees';
import PaymentsReconciliationTable from './PaymentsReconciliationTable';
import Dividend from './Dividend';
import InterestPayments from './InterestPayments';
import InvestmentBalanceAndValue from './InvestmentBalanceAndValue';
import { useDisclosure } from 'further-ui/hooks';
import { useInvestment } from 'hooks/data/investment/useInvestments';
import FeesRecalculationWarningModal from './FeesRecalculationWarningModal';
import UpdatedCommitmentErrorModal from './UpdatedCommitmentErrorModal';
import NegativeBalanceErrorModal from './NegativeBalanceErrorModal';
import TrancheChangeWarningModal from './TrancheChangeWarningModal';
import { usePayments } from 'hooks/data/investment/usePayments';
import { createAddInvestmentTransferRoute } from 'adminConstants/routes';
import useInvestmentDetails from 'hooks/data/investment/useInvestmentDetails';
import { useFund } from 'hooks/data/fund/useFund';
import Notes from 'components/Notes';
import { NoteRelation } from 'further-types/notes';
import { Api } from 'further-types/investment';
import { OverallPerformanceTabs } from './OverallPerformanceTabs';
import PageContent from 'components/PageContent';
import Heading from 'components/Heading';
import Button from 'components/Button';
import ButtonGroup from 'components/ButtonGroup';
import PageSection from 'components/PageSection';
import PageHeader from 'components/PageHeader';
import ResponsiveActions from 'components/PageHeader/ResponsiveActions';
import { Row } from 'components/Layout';
import AlertDialog from 'components/AlertDialog';
import UpdatedDefaultAdviserWarningModal from './UpdatedDefaultAdviserWarningModal';
import api from 'lib/trpcClient';
import Spacing from 'components/Spacing';
import InvestmentDateChangedWarningModal from './InvestmentDateChangedWarningModal';
import SubscriptionDateChangeWarningModal from './SubscriptionDateChangeWarningModal';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { dateChangeWillAffectFees } from 'further-ui/utils';

const breadcrumbs = [
  { label: 'Dashboard' },
  { label: 'Subscriptions', link: '/investment' },
  { label: 'View Subscription', link: '/', isActive: true },
];

export type Shareholding = Api.GetInvestmentShareholdingsResponse[number] & {
  isTransferred?: boolean;
};

export type Exits = Api.GetInvestmentExitsResponse['exits'];
export type InvestmentBalance =
  Api.GetInvestmentDetailsResponse['investmentBalance'];

//If it is a transferred shareholding we want to get the shares held at the time of transfer
const getTransferredShareholdings = (shareholding: Shareholding) => {
  return shareholding.transferredShareholdings.map(
    (transferredShareholding) => ({
      ...transferredShareholding,
      isTransferred: true,
      incomeTaxRelief: 0,
      transferredShareholdings: [],
      sharesCurrentlyHeld: transferredShareholding?.noOfSharesHistory?.length
        ? transferredShareholding.noOfSharesHistory[0]?.noOfShares
        : transferredShareholding.sharesCurrentlyHeld,
    }),
  );
};

const UpdateInvestment = ({ history }) => {
  const { id } = useParams<{ id: string }>();
  // disclosures
  const [updateAfterFeeCheck, setUpdateAfterFeeCheck] = useState(false);
  const feesRecalculationWarningDisclosure = useDisclosure();
  const updatedCommitmentErrorDisclosure = useDisclosure();
  const investmentDateChangedWarningDisclosure = useDisclosure();
  const subscriptionDateChangeWarningDisclosure = useDisclosure();
  const coreFeeTemplateChangeWarningDisclosure = useDisclosure();
  const trancheChangeWarningDisclosure = useDisclosure();
  const defaultAdviserDisclosure = useDisclosure();
  const negativeBalanceErrorDisclosure = useDisclosure();
  // state hooks
  const [editableDetails, setEditableDetails] =
    useState<EditableInvestmentDetails>({});
  const [initialTotalCommitment, setInitialTotalCommitment] = useState(0);
  const [initialCoreFeeTemplateId, setInitialCoreFeeTemplateId] = useState<
    string | undefined
  >();
  const [canEdit, canTransfer] = useHasPermissions([
    RolePermission.EditInvestment,
    RolePermission.TransferInvestment,
  ]);
  const debouncedTotalCommitment = useDebounce(
    editableDetails.totalCommitment,
    1000,
  );
  //data hooks
  const fetchPayments = usePayments(id);
  const { getFees, getExits, getShareholdings } = useInvestmentDetails();
  const { data: fees, ...feeRequest } = getFees(id);
  const { get: getFund } = useFund(editableDetails.fund?._id);
  const { data: exitResponse, ...exitRequest } = getExits(id);
  const { data: currentShareholdings } = getShareholdings(id);
  const apiUtils = api.useUtils();

  const isSyndicate = Boolean(getFund.data?.tag?.isSyndicate);

  const exits: Exits = exitResponse?.exits ?? [];

  const combinedShareholdings = useMemo(() => {
    return (currentShareholdings || [])?.reduce<
      Array<Shareholding & { isTransferred?: boolean }>
    >((combined, shareholding) => {
      return [
        ...combined,
        ...(shareholding.sharesCurrentlyHeld ? [shareholding] : []),
        ...getTransferredShareholdings(shareholding),
      ];
    }, []);
  }, [currentShareholdings]);

  const investmentIsAllocated = !!(
    combinedShareholdings?.length || exits?.length
  );

  const hasExits = !!exits?.length;

  const hasExportedFees = !!fees?.fees?.some(
    (fee) => fee.isExportedToCustodian,
  );

  const { fetch, update, canAffordNewFees } = useInvestment({
    investmentId: id,
    onNegativeBalanceError: negativeBalanceErrorDisclosure.onOpen,
    coreFeeTemplateId:
      editableDetails.coreFeeTemplateId === ''
        ? undefined
        : editableDetails.coreFeeTemplateId,
    investmentAmount: debouncedTotalCommitment,
    disableAffordNewFeesCheck: investmentIsAllocated || hasExportedFees,
  });

  const allowLeaveFeesUnchangedOption =
    (editableDetails?.totalCommitment ?? 0) >=
    (fetch.data?.investmentBalance?.deploymentSummary
      .cashFeesOnInitialInvestment ?? 0);

  const trancheChanged =
    editableDetails.fund?._id &&
    fetch.data?.fund._id !== editableDetails.fund?._id;

  useEffect(
    function fetchInvestment() {
      if (fetch.data) {
        setEditableDetails({
          adviser: fetch.data.adviser,
          adviserFee: fetch.data.adviserFee ?? 0,
          totalCommitment: fetch.data.totalCommitment ?? 0,
          externalInvestmentId: fetch.data.externalInvestmentId ?? '',
          coreFeeTemplateId: fetch.data.coreFeeTemplateId,
          performanceFeeTemplateId: fetch.data.performanceFeeTemplateId,
          customTags: fetch.data.customTags ?? [],
          additionalAdviserIds: fetch.data.additionalAdviserIds ?? [],
          fund: fetch.data.fund,
        });

        setInitialTotalCommitment(fetch.data.totalCommitment);
        setInitialCoreFeeTemplateId(fetch.data.coreFeeTemplateId);
      }
    },
    [id, fetch.data],
  );

  const investmentDetails = fetch.data;
  const { investmentBalance, interest, dividends } = investmentDetails || {};
  const { deploymentSummary } = investmentBalance || {};

  const handleInvestmentUpdate = ({
    recalculateFees,
    excludeDateUpdate = false,
  }: {
    recalculateFees?: boolean;
    excludeDateUpdate?: boolean;
  }) => {
    feesRecalculationWarningDisclosure.onClose();
    coreFeeTemplateChangeWarningDisclosure.onClose();

    const feeTemplateData: Pick<
      Api.UpdateInvestmentRequest,
      'coreFeeTemplateId' | 'performanceFeeTemplateId'
    > = {
      performanceFeeTemplateId:
        editableDetails.performanceFeeTemplateId ?? null,
      coreFeeTemplateId: editableDetails.coreFeeTemplateId ?? null,
    };

    const investmentDate = editableDetails.investmentDate
      ? fromZonedTime(
          startOfDay(editableDetails.investmentDate),
          'UTC',
        ).toISOString()
      : undefined;

    const values: Api.UpdateInvestmentRequest = {
      adviserId: editableDetails.adviser?._id || null,
      additionalAdviserIds:
        editableDetails.additionalAdviserIds?.filter((id) => id !== '') ?? [],
      externalInvestmentId: editableDetails.externalInvestmentId ?? '',
      adviserFee: editableDetails.adviserFee ?? 0,
      customTags: editableDetails.customTags ?? [],
      totalCommitment: editableDetails.totalCommitment ?? 0,
      ...(trancheChanged && { fundId: editableDetails.fund?._id ?? null }),
      ...feeTemplateData,
      recalculateFees,
      ...(!excludeDateUpdate && investmentDate && { investmentDate }),
    };

    update.mutate(values, {
      onSuccess: () => {
        apiUtils.investor.getInvestor.invalidate({
          investorId: investmentDetails?.investor?._id ?? '',
        });
        apiUtils.investor.listInvestors.invalidate();
      },
    });
  };

  const handleTrancheChange = ({
    recalculateFees,
  }: {
    recalculateFees?: boolean;
  }) => {
    trancheChangeWarningDisclosure.onClose();
    if (recalculateFees) {
      setUpdateAfterFeeCheck(true);
      setEditableDetails({
        ...editableDetails,
        performanceFeeTemplateId: getFund.data?.performanceFeeTemplateId,
        coreFeeTemplateId: getFund.data?.coreFeeTemplateId,
      });
    } else {
      handleInvestmentUpdate({ recalculateFees: false });
    }
  };

  const handleInvestmentDateChange = ({
    keepDateChange,
  }: {
    keepDateChange: boolean;
  }) => {
    investmentDateChangedWarningDisclosure.onClose();

    if (!keepDateChange) {
      setEditableDetails({
        ...editableDetails,
        investmentDate: investmentDetails?.investmentDate
          ? new Date(investmentDetails?.investmentDate)
          : undefined,
      });
    }

    void handleSave({
      skipDateWarning: true,
      excludeDateUpdate: !keepDateChange,
    });
  };

  const { data: investorDetail } = api.investor.getInvestor.useQuery(
    { investorId: investmentDetails?.investor?._id ?? '' },
    { enabled: !!investmentDetails?.investor?._id },
  );
  const defaultAdviserId = investorDetail?.defaultAdviserId;

  const handleSave = async ({
    skipDateWarning = false,
    excludeDateUpdate = false,
  }: {
    skipDateWarning?: boolean;
    excludeDateUpdate?: boolean;
  } = {}) => {
    const dateChanged =
      editableDetails.investmentDate &&
      editableDetails.investmentDate.toISOString() !==
        investmentDetails?.investmentDate;

    if (!skipDateWarning && dateChanged) {
      if (dateChangeWillAffectFees(fees?.fees)) {
        subscriptionDateChangeWarningDisclosure.onOpen();
        return;
      }
      investmentDateChangedWarningDisclosure.onOpen();
      return;
    }

    const commitmentOrFeeChanged =
      editableDetails?.totalCommitment !== initialTotalCommitment ||
      editableDetails?.adviserFee !== investmentDetails?.adviserFee;

    if (commitmentOrFeeChanged) {
      const hasTotalCommitmentError =
        round(
          (editableDetails?.totalCommitment ?? 0) +
            (editableDetails?.adviserFee ?? 0),
          2,
        ) < (deploymentSummary?.fundsReceived ?? 0);

      if (hasTotalCommitmentError) {
        updatedCommitmentErrorDisclosure.onOpen();
        return;
      }
    }

    if (defaultAdviserId && editableDetails.adviser?._id !== defaultAdviserId) {
      defaultAdviserDisclosure.onOpen();
      return;
    }

    // Total Commitment Updated and Core Fee Template unchanged
    if (
      editableDetails.totalCommitment !== initialTotalCommitment &&
      editableDetails.coreFeeTemplateId === initialCoreFeeTemplateId
    ) {
      feesRecalculationWarningDisclosure.onOpen();
      return;
    }

    const coreTemplateIdDiffersFromTrancheDefault =
      getFund.data?.coreFeeTemplateId !== editableDetails.coreFeeTemplateId;
    const performanceFeeTemplateIdDiffersFromTrancheDefault =
      getFund.data?.performanceFeeTemplateId !==
      editableDetails.performanceFeeTemplateId;
    if (
      trancheChanged &&
      (coreTemplateIdDiffersFromTrancheDefault ||
        performanceFeeTemplateIdDiffersFromTrancheDefault)
    ) {
      trancheChangeWarningDisclosure.onOpen();
      return;
    }

    if (editableDetails.coreFeeTemplateId !== initialCoreFeeTemplateId) {
      if (canAffordNewFees?.data) {
        coreFeeTemplateChangeWarningDisclosure.onOpen();
        return;
      } else {
        negativeBalanceErrorDisclosure.onOpen();
        return;
      }
    }

    handleInvestmentUpdate({ excludeDateUpdate });
  };

  useEffect(() => {
    if (!updateAfterFeeCheck || canAffordNewFees.isFetching) return;
    setUpdateAfterFeeCheck(false);
    if (canAffordNewFees.data) {
      handleInvestmentUpdate({ recalculateFees: true });
    } else {
      negativeBalanceErrorDisclosure.onOpen();
    }
  }, [updateAfterFeeCheck, canAffordNewFees.data, canAffordNewFees.isFetching]);

  if (fetch.isLoading) return <CircularProgress />;
  if (!fetch.data || !fetch.isSuccess) return <div>Subscription not found</div>;

  const isTransferDisabled = !!deploymentSummary?.heldByFundAwaitingInvestment;
  const transferDisabledTooltipText =
    "To transfer any holdings from this subscription, please first freeze the subscription and withdraw all this subscription's uninvested capital.";

  const onTransfer = () =>
    isTransferDisabled
      ? null
      : history.push(
          createAddInvestmentTransferRoute(fetch.data?.investor._id ?? ''),
        );

  return (
    <PageContainer
      heading="View Subscription"
      breadcrumbs={breadcrumbs}
      refetching={fetch.isRefetching}
    >
      <InvestmentDateChangedWarningModal
        disclosure={investmentDateChangedWarningDisclosure}
        isSyndicate={isSyndicate}
        onConfirm={handleInvestmentDateChange}
      />
      <SubscriptionDateChangeWarningModal
        disclosure={subscriptionDateChangeWarningDisclosure}
        onConfirm={() => {
          void handleSave({
            skipDateWarning: true,
          });
        }}
      />
      <FeesRecalculationWarningModal
        disclosure={feesRecalculationWarningDisclosure}
        onConfirm={handleInvestmentUpdate}
        allowLeaveFeesUnchangedOption={allowLeaveFeesUnchangedOption}
      />
      <TrancheChangeWarningModal
        disclosure={trancheChangeWarningDisclosure}
        onConfirm={handleTrancheChange}
      />
      <UpdatedCommitmentErrorModal
        disclosure={updatedCommitmentErrorDisclosure}
      />
      <AlertDialog
        size="sm"
        open={coreFeeTemplateChangeWarningDisclosure.isOpen}
        onClose={coreFeeTemplateChangeWarningDisclosure.onClose}
        title="Fee preset changed"
        content="Upon saving this change, all initial and recurring fees will be recalculated with the new fee preset as the basis."
        onConfirm={() => {
          handleInvestmentUpdate({});
        }}
      />
      <NegativeBalanceErrorModal
        disclosure={negativeBalanceErrorDisclosure}
        canAffordNewFees={canAffordNewFees.data}
      />
      <UpdatedDefaultAdviserWarningModal
        disclosure={defaultAdviserDisclosure}
        onConfirm={async () => {
          await handleInvestmentUpdate({ recalculateFees: false });
        }}
      />
      <PageContent withSpacing>
        <PageSection>
          <Spacing size="md" direction="column">
            <InvestmentDetails
              shareholdings={currentShareholdings ?? []}
              investmentDetails={investmentDetails}
              paymentExpectations={fetchPayments.data ?? []}
              editableDetails={editableDetails}
              setEditableDetails={setEditableDetails}
              fees={fees?.fees ?? []}
              investmentIsAllocated={investmentIsAllocated}
              hasExportedFees={hasExportedFees}
              calculatingNewFees={canAffordNewFees.isFetching}
              hasExits={hasExits}
            />
            <ButtonGroup>
              {canEdit && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => void handleSave({})}
                  loading={update.isPending || canAffordNewFees.isFetching}
                >
                  Update
                </Button>
              )}
            </ButtonGroup>
          </Spacing>
        </PageSection>
        <PageSection showDivider>
          <OverallPerformanceTabs
            investmentBalance={investmentBalance ?? null}
            combinedShareholdings={combinedShareholdings}
            exits={exits}
          />
        </PageSection>
        <PageSection showDivider withSpacing>
          <PageHeader>
            <Heading variant="h2" noMargin>
              Current shareholdings
            </Heading>
            <ResponsiveActions>
              <Tooltip
                title={isTransferDisabled ? transferDisabledTooltipText : ''}
              >
                <div>
                  {canTransfer && (
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={onTransfer}
                      disabled={isTransferDisabled}
                    >
                      Transfer holdings
                    </Button>
                  )}
                </div>
              </Tooltip>
            </ResponsiveActions>
          </PageHeader>
          <CurrentShareholdings
            combinedShareholdings={combinedShareholdings}
            transferDate={fetch.data?.transferDate}
          />
        </PageSection>
        <PageSection>
          <Heading variant="h2">Exited shareholdings</Heading>
          <ExitedShareholdings
            loading={exitRequest.isFetching}
            exitedShareholdings={exits}
          />
        </PageSection>
        <PageSection>
          <Heading variant="h2">Dividends</Heading>
          <Dividend
            loading={fetch.isFetching}
            dividends={dividends}
            firmName={investmentDetails?.firm.firmName}
            fundName={investmentDetails?.fund.fundName}
          />
        </PageSection>
        <PageSection>
          <Heading variant="h2">Interest received</Heading>
          <InterestPayments
            loading={fetch.isFetching}
            interestPayments={interest}
          />
        </PageSection>
        <PageSection>
          <Heading variant="h2">Initial and recurring fees</Heading>
          <CoreFees loading={feeRequest.isFetching} fees={fees?.fees ?? []} />
        </PageSection>
        <PageSection>
          <FutureFees
            loading={feeRequest.isFetching}
            fees={fees?.futureFees ?? []}
          />
        </PageSection>
        <PageSection>
          <Heading variant="h2">Subscription cash balance</Heading>
          <InvestmentBalanceAndValue
            investmentBalance={investmentBalance ?? undefined}
          />
        </PageSection>

        <PageSection showDivider>
          <Heading variant="h2">Funds received</Heading>
          {!fetchPayments.isLoading ? (
            <PaymentsReconciliationTable
              //@ts-expect-error
              investmentId={investmentDetails?._id}
              //@ts-expect-error
              firmId={investmentDetails?.firm._id}
              payments={fetchPayments.data ?? []}
              unreconcilableAmount={round(
                (deploymentSummary?.purchasePriceOfAllShareholdings ?? 0) +
                  (deploymentSummary?.exportedCashFeesOnInitialInvestment ?? 0),
                2,
              )}
            />
          ) : (
            <Row justify="center">
              <CircularProgress size={24} />
            </Row>
          )}
        </PageSection>
        <PageSection showDivider>
          <Notes relationId={id} noteRelation={NoteRelation.Investment} />
        </PageSection>
      </PageContent>
    </PageContainer>
  );
};

export default UpdateInvestment;
